<template>
  <div class="base-list">
    <!-- Top Row Controls -->
    <div class="top-row-controls">
      <input
        type="text"
        v-model="searchText"
        @input="onSearchChange"
        :placeholder="searchPlaceholder"
        class="search-input"
      />

      <span class="results-info">
        {{filteredCount}} of {{totalCount}} results ({{pagination.totalPages}} pages)
      </span>

      <div class="pagination-controls">
        <button @click="changePage(1)" :disabled="currentPage === 1" title="First">First</button>
        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" title="Previous">&lt;</button>
        <span class="page-info">Page {{currentPage}} / {{pagination.totalPages}}</span>
        <button @click="changePage(currentPage + 1)" :disabled="currentPage === pagination.totalPages" title="Next">&gt;</button>
        <button @click="changePage(pagination.totalPages)" :disabled="currentPage === pagination.totalPages" title="Last">Last</button>
      </div>

      <select v-model="itemsPerPage" @change="onItemsPerPageChange" class="items-per-page">
        <option :value="25">25 results/page</option>
        <option :value="50">50 results/page</option>
        <option :value="100">100 results/page</option>
        <option :value="250">250 results/page</option>
      </select>
    </div>

    <!-- Table Slot -->
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'BaseList',
  props: {
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    filteredCount: {
      type: Number,
      required: true
    },
    totalCount: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    // When true, use server-side pagination behaviour: compute total pages from totalCount
    // When false (default), assume client-side pagination and compute from filteredCount
    serversidePagination: {
      type: Boolean,
      default: false
    },
    itemsPerPageProp: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      searchText: '',
      itemsPerPage: this.itemsPerPageProp
    };
  },
  computed: {
    pagination() {
      // Choose source for total pages based on whether server-side pagination is enabled.
      // If serversidePagination is true, totalCount should be the overall server total.
      // Otherwise, use filteredCount (client-side filtered set length).
      const baseCount = this.serversidePagination ? this.totalCount : this.filteredCount;
      const totalPages = Math.max(1, Math.ceil(baseCount / this.itemsPerPage));
      return {
        totalPages: totalPages,
        hasNextPage: this.currentPage < totalPages,
        hasPrevPage: this.currentPage > 1
      };
    }
  },
  methods: {
    onSearchChange() {
      this.$emit('search', this.searchText);
    },
    changePage(newPage) {
      if (newPage < 1 || newPage > this.pagination.totalPages) return;
      this.$emit('page-change', newPage);
    },
    onItemsPerPageChange() {
      this.$emit('items-per-page-change', this.itemsPerPage);
    }
  },
  watch: {
    itemsPerPageProp(newVal) {
      this.itemsPerPage = newVal;
    }
  }
};
</script>

<style scoped>
.base-list {
  width: 100%;
}

.top-row-controls {
  display: flex;
  align-items: center;
  background: #e9e9e9;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  flex-wrap: wrap;
}

.top-row-controls select,
.top-row-controls input,
.top-row-controls button {
  margin-bottom: 0;
}

.top-row-controls select,
.top-row-controls input {
  background-color: #fff;
}

.search-input {
  flex: 1;
  min-width: 150px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
}

.pagination-controls button {
  padding: 0 0.5rem;
  font-size: 0.9rem;
}

.results-info {
  font-size: 0.9rem;
  white-space: nowrap;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  padding: 0 0.5rem;
  font-size: 0.9rem;
  font-weight: bold;
  white-space: nowrap;
}

.items-per-page {
  padding: 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  width: fit-content;
}
</style>
